/* Loop purchase selector: native allocations, exact group/cadence/economic parity. */
(function (root) {
  'use strict';
  const LOOP_APP = '5284869';
  function cadence(plan) {
    const values = [plan.name, ...(plan.options || []).map(x => x.value || x)];
    for (const text of values) {
      const match = String(text || '').match(/(?:df:|every\s+)(\d*)\s*(day|week|month|year)s?/i);
      if (match) return { unit: match[2].toUpperCase(), count: Number(match[1] || 1) };
    }
    return null;
  }
  function cadenceKey(plan) { const c = cadence(plan); return c ? `${c.count}:${c.unit}` : ''; }
  function frequency(plan) {
    const c = cadence(plan);
    if (!c) throw new Error('Unrecognised delivery frequency');
    return `Every ${c.count === 1 ? '' : c.count + ' '}${c.unit.toLowerCase()}${c.count === 1 ? '' : 's'}`;
  }
  function choices(product, variantId) {
    const variant = product.variants.find(v => String(v.id) === String(variantId));
    if (!variant) throw new Error('Selected variant is not part of this product');
    const groups = product.selling_plan_groups || [];
    const allocated = new Map((variant.selling_plan_allocations || []).map(a => [String(a.selling_plan_id), a]));
    const previous = [];
    for (const group of groups.filter(g => String(g.app_id) === 'juo-subscriptions')) {
      for (const plan of group.selling_plans || []) {
        const allocation = allocated.get(String(plan.id));
        if (allocation) previous.push({ group, plan, allocation });
      }
    }
    const out = [];
    for (const group of groups.filter(g => String(g.app_id) === LOOP_APP)) {
      for (const plan of group.selling_plans || []) {
        const allocation = allocated.get(String(plan.id));
        if (!allocation || !cadenceKey(plan) || !plan.recurring_deliveries) continue;
        const sameEcon = p => p.allocation.price === allocation.price && JSON.stringify((p.allocation.price_adjustments || []).map(a => a.price)) === JSON.stringify((allocation.price_adjustments || []).map(a => a.price));
        // Match the replaced Juo plan by cadence + exact economics (group names are customer labels and may change).
        const parity = previous.find(p => p.group.name === group.name && cadenceKey(p.plan) === cadenceKey(plan)) || previous.find(p => cadenceKey(p.plan) === cadenceKey(plan) && sameEcon(p));
        // Wrong-pack assignments and changed economics never become selectable.
        if (previous.length && (!parity || parity.allocation.price !== allocation.price ||
            JSON.stringify((parity.allocation.price_adjustments || []).map(a => a.price)) !==
            JSON.stringify((allocation.price_adjustments || []).map(a => a.price)))) continue;
        out.push({ plan, allocation, group, recommended: /recommended|most common/i.test((parity || {plan}).plan.name), key: cadenceKey(plan) });
      }
    }
    return out;
  }
  function preferred(options, desiredKey) {
    return options.find(p => p.key === desiredKey) || options.find(p => p.recommended) || options[0] || null;
  }
  function monthlyPlan(product, variantId, expectedPricePence) {
    const options = choices(product, variantId).filter(p => p.key === '1:MONTH');
    if (options.length !== 1) throw new Error('Monthly Loop mapping missing or ambiguous for variant ' + variantId);
    const allocation = options[0].allocation;
    const variant = product.variants.find(v => String(v.id) === String(variantId));
    if (variant.price !== expectedPricePence || allocation.price !== Math.round(expectedPricePence * 0.85))
      throw new Error('Loop builder price differs from the approved offer');
    if ((allocation.price_adjustments || []).some(p => p.price !== allocation.price))
      throw new Error('Loop builder recurring price differs from the approved offer');
    return options[0].plan.id;
  }
  const model = { cadence, cadenceKey, frequency, choices, preferred, monthlyPlan };
  if (typeof module !== 'undefined' && module.exports) module.exports = model;
  root.IBLoopModel = model;
  if (!root.customElements || !root.HTMLElement || root.customElements.get('ib-loop-purchase')) return;
  class IBLoopPurchase extends root.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const json = this.querySelector('[data-loop-product]');
      if (!json) return;
      this.initialized = true;
      this.product = JSON.parse(json.textContent);
      this.form = document.getElementById(this.getAttribute('form'));
      if (!this.form) { this.initialized = false; return; }
      this.field = this.querySelector('[data-loop-plan-field]');
      this.select = this.querySelector('[data-loop-frequency]');
      this.intent = this.dataset.defaultMode === 'SUBSCRIPTION' || this.product.requires_selling_plan ? 'sub' : 'otp';
      this.userCadence = '';
      this.onVariant = () => queueMicrotask(() => this.refresh());
      this.onSubmit = event => {
        try { this.writePlan(); if (this.blocked) throw new Error(this.error); }
        catch (error) { event.preventDefault(); event.stopImmediatePropagation(); this.showError(error.message); }
      };
      this.onInput = event => {
        if (event.target === this.select) this.userCadence = this.select.selectedOptions[0]?.dataset.cadence || '';
        if (event.target.matches('[data-loop-mode]')) this.intent = event.target.value;
        this.refresh();
      };
      this.onClick = event => {
        const button = event.target.closest?.('button[type="submit"],input[type="submit"]');
        if (button?.form === this.form && this.blocked) {
          event.preventDefault(); event.stopImmediatePropagation(); this.showError(this.error);
        }
      };
      document.addEventListener('click', this.onClick, true);
      this.form.addEventListener('variant:change', this.onVariant);
      this.form.addEventListener('change', this.onVariant);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.addEventListener('change', this.onInput);
      this.refresh();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onClick, true);
      this.form?.removeEventListener('variant:change', this.onVariant);
      this.form?.removeEventListener('change', this.onVariant);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.removeEventListener('change', this.onInput);
      this.initialized = false;
    }
    money(pence) { return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' }).format(pence / 100); }
    showError(text) { const node=this.querySelector('[data-loop-error]'); node.textContent=text; node.hidden=!text; }
    applyPalette() {
      // Use the exact product form's native CTA, never a flavour-name colour guess.
      const button=Array.from(this.form.elements).find(e=>e.matches?.('button[type="submit"]'));
      const style=button ? getComputedStyle(button) : null;
      const token=style?.getPropertyValue('--button-background').trim().split('/')[0].trim();
      const base=token ? 'rgb('+token+')' : '';
      const colour=base && CSS.supports('color',base) ? base : style?.backgroundColor;
      if (colour && colour!=='transparent' && colour!=='rgba(0, 0, 0, 0)' && CSS.supports('color',colour)) {
        const probe=document.createElement('span'); probe.style.color=colour;
        this.style.setProperty('--ib-loop-accent',probe.style.color);
        this.dataset.paletteSource='native-product-cta-token';
      }
    }
    refresh() {
      this.applyPalette();
      const id = this.form.elements.namedItem('id')?.value;
      this.variant = this.product.variants.find(v => String(v.id) === String(id));
      if (!this.variant) { this.blocked=false; this.error=''; this.showError(''); this.field.disabled=true; this.field.value=''; this.hidden=!!id; return; }
      this.hidden=false;
      this.options = choices(this.product, id);
      const selected = preferred(this.options, this.userCadence);
      this.select.replaceChildren(...this.options.map(choice => {
        const option=document.createElement('option'); option.value=String(choice.plan.id); option.dataset.cadence=choice.key;
        option.textContent=frequency(choice.plan);
        option.selected=choice===selected; return option;
      }));
      const sub=this.querySelector('[data-loop-mode="sub"]'), otp=this.querySelector('[data-loop-mode="otp"]');
      const oneTimeOnly=!this.product.requires_selling_plan && !this.product.variants.some(v=>(v.selling_plan_allocations || []).length);
      // A limited label is not an eligibility rule: valid allocated plans remain offered.
      if (oneTimeOnly) this.intent='otp';
      sub.closest('.ib-loop-option').hidden=oneTimeOnly;
      const heading=this.querySelector('.ib-loop-heading'); if (heading) heading.hidden=oneTimeOnly;
      // A case with no valid plan falls back to one-time without forgetting the shopper's choice.
      const mode=(!this.options.length && !this.product.requires_selling_plan) ? 'otp' : this.intent;
      sub.disabled=!this.options.length; sub.checked=mode==='sub'; otp.checked=mode==='otp';
      otp.closest('.ib-loop-option').hidden=!!this.product.requires_selling_plan;
      this.querySelector('[data-loop-detail]').hidden=mode!=='sub' || !selected;
      this.querySelector('[data-loop-case]').textContent=this.variant.title;
      this.querySelector('[data-loop-otp-price]').textContent=this.money(this.variant.price);
      this.querySelector('[data-loop-sub-price]').textContent=selected ? this.money(selected.allocation.price) : 'Unavailable';
      const perDelivery=this.querySelector('[data-loop-per-delivery]'); if (perDelivery) perDelivery.hidden=!selected;
      const saving=selected ? Math.round(100*(this.variant.price-selected.allocation.price)/this.variant.price) : 0;
      const badge=this.querySelector('[data-loop-saving]'); badge.textContent=''; badge.hidden=true;
      const subTitle=this.querySelector('[data-loop-sub-title]'); if(subTitle) subTitle.textContent=saving>0 ? 'Save '+saving+'% with automatic refills' : 'Automatic refills';
      const later=selected ? (selected.allocation.price_adjustments || []).find(a => a.price!==selected.allocation.price) : null;
      if (later) {badge.textContent='INTRO OFFER'; badge.hidden=false;}
      this.querySelector('[data-loop-sub-copy]').textContent=later ? 'First delivery. Then '+this.money(later.price)+' per delivery.' : '';
      this.querySelector('[data-loop-repeat]').textContent=selected ? 'No commitment. Cancel anytime.' : '';
      for (const card of this.querySelectorAll('.ib-loop-option')) card.classList.toggle('is-selected',!!card.querySelector('input:checked'));
      this.writePlan();
    }
    writePlan() {
      const id=this.form.elements.namedItem('id')?.value;
      const current=this.product.variants.find(v => String(v.id)===String(id));
      if (!current) { this.blocked=false; this.error=''; this.field.disabled=true; this.field.value=''; return; }
      const options=choices(this.product,id);
      const choice=options.find(p => String(p.plan.id)===String(this.select.value));
      const mode=this.querySelector('[data-loop-mode]:checked')?.value;
      this.blocked=!current || (mode==='sub' && !choice) || (this.product.requires_selling_plan && mode!=='sub');
      this.error=this.blocked ? 'Subscriptions are not available for this case. Choose another case'+(this.product.requires_selling_plan ? '.' : ' or one-time purchase.') : '';
      this.field.disabled=mode!=='sub' || this.blocked;
      this.field.value=mode==='sub' && choice && !this.blocked ? String(choice.plan.id) : '';
      if (Array.from(this.form.elements).some(e => e.name==='selling_plan' && e!==this.field && !e.disabled && e.value)) {
        this.blocked=true; this.error='Please reload this page before adding to cart.'; this.field.disabled=true;
      }
      this.dataset.ready=String(!this.blocked);
      for (const button of Array.from(this.form.elements).filter(e => e.matches?.('button[type="submit"],input[type="submit"]'))) {
        if (this.blocked && !button.disabled) { button.dataset.loopBlocked='true'; button.disabled=true; }
        else if (!this.blocked && button.dataset.loopBlocked==='true') {
          if (current?.available) button.disabled=false;
          delete button.dataset.loopBlocked;
        }
      }
      // This is a storefront estimate using the existing 11-points-per-pound display rule,
      // not a BON balance, awarded reward, shipping/discount entitlement or gift promise.
      const paid=mode==='sub' && choice && !this.blocked ? choice.allocation.price : current?.price;
      const quantity=Math.max(1,Number(this.form.elements.namedItem('quantity')?.value || 1));
      const scope=this.closest('.product-info');
      for (const node of scope?.querySelectorAll('.labs-badge__points') || []) {
        node.hidden=!!this.blocked;
        node.textContent=this.blocked ? '' : String(Math.round(paid*quantity*11/100));
        if (node.parentElement.firstChild?.nodeType===3) node.parentElement.firstChild.textContent=this.blocked ? 'Earn ' : 'Earn an estimated ';
        node.dataset.loopEstimate='true';
      }
      this.showError(this.error);
    }
  }
  root.customElements.define('ib-loop-purchase',IBLoopPurchase);
})(typeof window === 'undefined' ? globalThis : window);
